import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, arrayUnion, Timestamp, onSnapshot, getDocs, collection, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { getAuth } from 'firebase/auth';
import { X, Send, Paperclip, Users } from 'lucide-react';
import eventBus from '../../utils/eventBus';

// Système de couleurs par rôle
const ROLE_COLORS = {
  'Super Admin': { bubble: 'bg-purple-100 border-purple-300', avatar: 'bg-purple-500' },
  'Chef de Projet': { bubble: 'bg-blue-100 border-blue-300', avatar: 'bg-blue-500' },
  'Chargé d\'Affaire': { bubble: 'bg-green-100 border-green-300', avatar: 'bg-green-500' },
  'Client': { bubble: 'bg-yellow-100 border-yellow-300', avatar: 'bg-yellow-500' },
  'Développeur': { bubble: 'bg-red-100 border-red-300', avatar: 'bg-red-500' },
  'Utilisateur': { bubble: 'bg-gray-100 border-gray-300', avatar: 'bg-gray-500' }
};

const getColorsByRole = (role) => ROLE_COLORS[role] || ROLE_COLORS['Utilisateur'];

const UnifiedMessageModal = ({ isOpen, onClose, discussionData, discussionId, customCollection = null, isAdmin = false }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [localDiscussionData, setLocalDiscussionData] = useState(discussionData);
  const [userRoles, setUserRoles] = useState({});
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasMarkedAsReadRef = useRef(false);

  const auth = getAuth();

  // 🔧 NOUVELLE LOGIQUE : Marque les MESSAGES INDIVIDUELS comme lus
  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (!discussionId || !auth.currentUser?.uid || markingAsRead || hasMarkedAsReadRef.current) {
        return;
      }

      try {
        setMarkingAsRead(true);
        hasMarkedAsReadRef.current = true;
        
        console.log('🔄 MESSAGERIE MODAL: Marquage messages individuels comme lus:', discussionId);
        
        const discussionRef = doc(db, 'discussions', discussionId);
        const docSnap = await getDoc(discussionRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const messages = data.messages || [];
          
          // 🔧 NOUVELLE LOGIQUE : Marquer chaque message individuel comme lu
          let hasUnreadMessages = false;
          const updatedMessages = messages.map(message => {
            const readBy = message.readBy || [];
            
            // Si le message n'est pas créé par l'utilisateur actuel ET pas encore lu
            if (message.createdBy !== auth.currentUser.uid && !readBy.includes(auth.currentUser.uid)) {
              hasUnreadMessages = true;
              return {
                ...message,
                readBy: [...readBy, auth.currentUser.uid]
              };
            }
            
            return message;
          });
          
          // Mettre à jour seulement s'il y a des messages non lus
          if (hasUnreadMessages) {
            console.log('📝 MESSAGERIE MODAL: Mise à jour des messages individuels...');
            
            await updateDoc(discussionRef, {
              messages: updatedMessages,
              readBy: arrayUnion(auth.currentUser.uid) // Marquer aussi la discussion
            });
            
            console.log('✅ SUCCESS: Messages individuels marqués comme lus');
            
            // Délai avant émission de l'événement pour éviter conflicts
            setTimeout(() => {
              eventBus.emit('forceRefreshNotificationCounts');
            }, 500);
          } else {
            console.log('ℹ️ DEBUG: Tous les messages sont déjà marqués comme lus');
          }
        }
        
      } catch (error) {
        console.error('❌ ERROR: Erreur lors du marquage des messages:', error);
        hasMarkedAsReadRef.current = false; // Reset on error
      } finally {
        setMarkingAsRead(false);
      }
    };

    if (isOpen && !hasMarkedAsReadRef.current) {
      // Délai pour éviter conflits avec autres listeners
      const timeoutId = setTimeout(markMessagesAsRead, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, discussionId, auth.currentUser]);

  // Reset du flag quand modal se ferme
  useEffect(() => {
    if (!isOpen) {
      hasMarkedAsReadRef.current = false;
    }
  }, [isOpen]);

  // Log d'ouverture modal
  useEffect(() => {
    if (isOpen && discussionId) {
      console.log('TARGET: Modal ouverte pour discussion:', discussionId);
    }
  }, [isOpen, discussionId]);

  // Chargement des rôles utilisateurs
  useEffect(() => {
    if (!isOpen) return;

    const fetchUserRoles = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const roles = {};
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          roles[doc.id] = {
            role: userData.role || 'Utilisateur',
            email: userData.email,
            displayName: userData.displayName || userData.email
          };
          
          if (userData.email) {
            roles[userData.email] = {
              role: userData.role || 'Utilisateur',
              id: doc.id,
              displayName: userData.displayName || userData.email
            };
          }
        });
        
        setUserRoles(roles);
        console.log('SUCCESS: Roles utilisateurs charges:', Object.keys(roles).length, 'utilisateurs');
      } catch (error) {
        console.error('ERROR: Erreur lors de la recuperation des roles:', error);
      }
    };
    
    fetchUserRoles();
  }, [isOpen]);

  // Listener en temps réel pour les mises à jour - VERSION SÉCURISÉE
  useEffect(() => {
    if (!discussionId || !isOpen) return;

    console.log('DEBUG: Mise en place du listener pour discussion:', discussionId);
    
    let unsubscribeFunction = null;
    
    const setupListener = () => {
      const docRef = doc(db, 'discussions', discussionId);
      
      unsubscribeFunction = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setLocalDiscussionData(data);
          console.log('SUCCESS: Donnees discussion mises a jour:', data.messages?.length, 'messages');
        } else {
          console.warn('WARNING: Discussion introuvable:', discussionId);
        }
      }, (error) => {
        console.error("ERROR: Erreur listener discussion:", error);
        // En cas d'erreur, retry après délai
        setTimeout(setupListener, 2000);
      });
    };

    // Délai pour éviter conflits avec le marquage
    const timeoutId = setTimeout(setupListener, 100);

    return () => {
      clearTimeout(timeoutId);
      if (unsubscribeFunction) {
        console.log('DEBUG: Nettoyage listener discussion');
        unsubscribeFunction();
      }
    };
  }, [discussionId, isOpen]);

  // Scroll automatique vers le bas
  useEffect(() => {
    if (messagesEndRef.current && localDiscussionData?.messages?.length) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [localDiscussionData?.messages?.length]);

  // Fonctions utilitaires
  const getUserRole = (userId, userEmail) => {
    if (userId && userRoles[userId]?.role) {
      return userRoles[userId].role;
    }
    if (userEmail && userRoles[userEmail]?.role) {
      return userRoles[userEmail].role;
    }
    return 'Utilisateur';
  };

  const normalizeRoleName = (role) => {
    if (!role) return 'Utilisateur';
    
    const roleLower = role.toLowerCase();
    
    if (roleLower === 'superadmin' || roleLower === 'super-admin' || roleLower === 'admin') {
      return 'Super Admin';
    }
    if (roleLower === 'projectmanager' || roleLower === 'project-manager' || roleLower === 'chef de projet') {
      return 'Chef de Projet';
    }
    if (roleLower === 'charge-affaire' || roleLower === 'chargé d\'affaire' || roleLower === 'chargeaffaire') {
      return 'Chargé d\'Affaire';  
    }
    if (roleLower === 'client' || roleLower === 'customer') {
      return 'Client';
    }
    
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Upload de fichier
  const uploadFileToStorage = async (file) => {
    try {
      setUploading(true);
      console.log('DEBUG: Upload fichier:', file.name, file.size, 'bytes');
      
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `attachments/${auth.currentUser.uid}/${fileName}`;
      const storageRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(uploadResult.ref);
      
      console.log('SUCCESS: Fichier uploade:', fileUrl);
      
      return {
        name: file.name,
        url: fileUrl,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error("ERROR: Erreur upload:", error);
      alert("Erreur lors de l'upload du fichier: " + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Envoi de message - VERSION SÉCURISÉE
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!newMessage.trim() && !selectedFile) || sending || !discussionId) {
      console.warn('WARNING: Conditions d\'envoi non remplies:', {
        hasMessage: !!newMessage.trim(),
        hasFile: !!selectedFile,
        sending,
        discussionId
      });
      return;
    }
    
    if (!auth.currentUser) {
      alert("Erreur: Vous devez être connecté pour envoyer un message");
      return;
    }
    
    try {
      setSending(true);
      console.log('DEBUG: Debut envoi message...');
      
      let attachment = null;
      if (selectedFile) {
        attachment = await uploadFileToStorage(selectedFile);
        if (!attachment) return;
      }
      
      const messageId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`;
      
      const newMessageObj = {
        id: messageId,
        text: newMessage.trim() || `Fichier: ${selectedFile?.name}`,
        createdBy: auth.currentUser.uid,
        createdByEmail: auth.currentUser.email,
        createdAt: Timestamp.now(),
        readBy: [auth.currentUser.uid],
        ...(attachment && { attachment })
      };

      console.log('DEBUG: Envoi vers discussions/' + discussionId);
      const docRef = doc(db, 'discussions', discussionId);
      
      // VERSION SÉCURISÉE: mise à jour atomique
      await updateDoc(docRef, {
        messages: arrayUnion(newMessageObj),
        updatedAt: Timestamp.now(),
        lastMessage: newMessageObj.text,
        readBy: [auth.currentUser.uid] // Reset readBy de façon sécurisée
      });
      
      console.log('SUCCESS: Message envoye avec succes!');
      
      // Reset du formulaire (le listener Firebase mettra à jour l'état automatiquement)
      setNewMessage('');
      setSelectedFile(null);
      
      // Scroll vers le bas après un court délai
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
      
    } catch (error) {
      console.error('ERROR: Erreur lors de l\'envoi du message:', error);
      alert(`Erreur lors de l'envoi: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const currentDiscussion = localDiscussionData || discussionData;

  if (!isOpen || !currentDiscussion) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 h-5/6 flex flex-col">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{currentDiscussion.dossierTitre || currentDiscussion.title}</h2>
            <div className="flex items-center space-x-2 mt-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">
                {currentDiscussion.participants?.length || 0} participants
              </span>
              {markingAsRead && (
                <span className="text-xs text-blue-500">Marquage messages en cours...</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {currentDiscussion.messages && currentDiscussion.messages.length > 0 ? (
            currentDiscussion.messages.map((message, index) => {
              const isCurrentUser = message.createdBy === auth.currentUser?.uid;
              const isSystemMessage = message.system === true;
              
              // Détermination du rôle
              let roleName = 'Utilisateur';
              
              if (isSystemMessage) {
                roleName = 'Système';
              } else if (message.createdBy === auth.currentUser?.uid) {
                const currentUserRole = getUserRole(auth.currentUser.uid, auth.currentUser.email);
                roleName = normalizeRoleName(currentUserRole || 'Super Admin');
              } else {
                const messageUserRole = getUserRole(message.createdBy, message.createdByEmail);
                roleName = normalizeRoleName(messageUserRole || 'Client');
              }
              
              const colors = getColorsByRole(roleName);
              
              if (isSystemMessage) {
                return (
                  <div key={`${message.id || index}-${message.createdAt?.seconds || Date.now()}`} className="text-center">
                    <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg inline-block text-sm">
                      {message.text}
                    </div>
                  </div>
                );
              }

              return (
                <div key={`${message.id || index}-${message.createdAt?.seconds || Date.now()}`} className={`flex items-start space-x-3 ${isCurrentUser ? 'justify-end' : ''}`}>
                  {!isCurrentUser && (
                    <div className={`w-8 h-8 rounded-full ${colors.avatar} flex items-center justify-center text-white text-xs`}>
                      {roleName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 max-w-md">
                    <div className={`${isCurrentUser ? 'bg-blue-500 text-white' : colors.bubble} p-3 rounded-lg border`}>
                      <div className={`flex items-center justify-between text-xs ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'} mb-1`}>
                        <span>
                          {roleName} • {formatDate(message.createdAt)}
                        </span>
                      </div>
                      <p className={`text-sm ${isCurrentUser ? 'text-white' : 'text-gray-700'}`} style={{ whiteSpace: 'pre-line' }}>
                        {message.text}
                      </p>
                      
                      {message.attachment && (
                        <div className={`mt-2 p-2 rounded ${isCurrentUser ? 'bg-blue-400' : 'bg-white'} border`}>
                          <div className="flex items-center space-x-2">
                            <Paperclip className="w-4 h-4" />
                            <a 
                              href={message.attachment.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className={`text-sm underline ${isCurrentUser ? 'text-blue-100' : 'text-blue-600'}`}
                            >
                              {message.attachment.name}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {isCurrentUser && (
                    <div className={`w-8 h-8 rounded-full ${colors.avatar} flex items-center justify-center text-white text-xs`}>
                      {roleName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-4 text-gray-500">
              Aucun message pour le moment
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Zone de saisie */}
        <form onSubmit={handleSendMessage} className="p-4 border-t bg-gray-50">
          {selectedFile && (
            <div className="mb-2 p-2 bg-blue-50 rounded flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Paperclip className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">Fichier: {selectedFile.name}</span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                className="text-blue-700 hover:text-blue-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="flex items-end space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.csv"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Tapez votre message..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="2"
                disabled={sending || uploading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
            </div>
            
            <button
              type="submit"
              disabled={(!newMessage.trim() && !selectedFile) || sending || uploading}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending || uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>

        {/* Debug info en bas */}
        <div className="px-4 py-2 bg-gray-100 text-xs text-gray-500 border-t">
          Mode Admin | Messages: {currentDiscussion?.messages?.length || 0} | ID: {discussionId?.slice(-8)} | Read: {markingAsRead ? 'Processing Individual Messages...' : 'Ready'}
        </div>
      </div>
    </div>
  );
};

export default UnifiedMessageModal;